import { IListItem } from '../../../../interfaces/listItem';
import { IEducation } from '../../../../interfaces/education';
import { useCallback, useContext, useMemo } from 'react';
import { ColumnBoxFlexFullWidth, FlexBox, FlexBoxFullWidth } from '../../../common/boxes';
import { Typography } from '@mui/material';
import { MONTH_NAMES } from '../common/ExperienceOrEducationEntityModal/Dates';
import { TEXTS } from '../../../../core/texts';
import { useSearchParams } from 'react-router-dom';
import ProfileListItem from '../common/ProfileListItem';
import { TrendingFlat } from '@mui/icons-material';
import { FreelancerContext } from '../FreelancerEntity';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FreelancerService } from '../../../../services/freelancer';
import { useNotification } from '../../../../+state/useNotification';
import { AxiosError } from 'axios';
import { IErrorResponse } from '../../../../interfaces/errorResponse';
import { FREELANCER_ENTITY_QUERY_KEY } from '../../../../queryHooks/freelancer/useGetFreelancerEntity';
import { useGetCountryList } from '../../../../queryHooks/useGetCountryList';

export const EDUCATION_QUERY_PARAM = 'education';

const EducationListItem = ({ entity }: IListItem<IEducation>) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useContext(FreelancerContext);
  const { openSuccessToast, openErrorToast } = useNotification();
  const queryClient = useQueryClient();
  const { data: countriesList } = useGetCountryList();
  const countryList = useMemo(() => countriesList || [], [countriesList]);

  const mutation = useMutation({
    mutationFn: (educationId: string) => FreelancerService.deleteEducation(user.id, educationId),
    onSuccess: () => {
      openSuccessToast('Successfully deleted');
      queryClient.invalidateQueries([FREELANCER_ENTITY_QUERY_KEY]);
    },
    onError: (err: AxiosError<IErrorResponse>) => {
      openErrorToast(err.response?.data.message || err.message);
    }
  });

  const countryName = useMemo(() =>
    countryList.find(c => c.countryISO3code === entity.country)?.name || entity.country, [countryList, entity.country]);

  const handleClickEdit = useCallback(() => {
    searchParams.set(EDUCATION_QUERY_PARAM, entity.id!.toString());
    setSearchParams(searchParams);
  }, [searchParams, setSearchParams, entity.id]);

  const deleteEducationHandler = useCallback(() => {
    mutation.mutate(entity.id!);
  }, [mutation, entity.id]);

  return (
    <ProfileListItem deleteHandler={deleteEducationHandler} editHandler={handleClickEdit}>
      <ColumnBoxFlexFullWidth>
        <Typography variant={'h6'}>
          {entity.name}
        </Typography>
        <Typography variant={'h4'}>
          {entity.institution}
        </Typography>
        <FlexBoxFullWidth alignItems={'center'} mt={1}>
          <FlexBox>
            <Typography>
              {entity.startDateMonth && entity.startDateYear && MONTH_NAMES[entity.startDateMonth - 1].slice(0, 3) + "-"}
            </Typography>
            <Typography>
              {entity.startDateYear ?? "N/A"}
            </Typography>
          </FlexBox>
          <TrendingFlat sx={{ mx: 1 }}/>
          {
            entity.endDateYear && entity.endDateMonth
              ? <FlexBox>
                <Typography>
                  {MONTH_NAMES[entity.endDateMonth - 1].slice(0, 3)}-
                </Typography>
                <Typography>
                  {entity.endDateYear}
                </Typography>
              </FlexBox>
              : <Typography>
                {TEXTS.UNTIL_NOW}
              </Typography>
          }
        </FlexBoxFullWidth>
        <FlexBox>
          <Typography>
            {entity.town && (entity.town + ', ') + countryName}
          </Typography>
        </FlexBox>
        <Typography variant={'body2'} sx={{ my: 2 }}>
          {entity.description}
        </Typography>
      </ColumnBoxFlexFullWidth>
    </ProfileListItem>
  );
};

export default EducationListItem;